import React, { useState, useMemo } from "react";
import "./FlightRecon.scss";
import PageHeader from "app/shared/PageHeader";
import { AgTableClient } from "app/shared/AgTable";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  RightCircleOutlined,
} from "@ant-design/icons";
import ToolParamInvoker from "app/shared/AgTable/HelperTools/ToolParamInvoker";
import { handleCellRenderer } from "app/shared/AgTable/AgUtility";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

import JsonData from "./demo.json";
import dividePNRData from "./dividePNRData.json";
import baseBooking3Data from "./baseBooking3Data.json";
import baseBooking4Data from "./baseBooking4Data.json";
import { useRecoilState } from "recoil";
import { currentPDFData } from "app/config/States/users";
import { Button, Tag, Tooltip } from "antd";
import Typography from "app/shared/Typography";
import PendingIcon from "@mui/icons-material/Pending";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import UnsubscribeOutlinedIcon from "@mui/icons-material/UnsubscribeOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import MarkunreadMailboxOutlinedIcon from "@mui/icons-material/MarkunreadMailboxOutlined";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import { colorPicker } from "app/utils/color.helper";
import moment from "moment";
function generateUAEVATID() {
  let vatId = "";
  for (let i = 0; i < 15; i++) {
    vatId += Math.floor(Math.random() * 10);
  }
  return vatId;
}

const colorOfEmailEvent: any = {
  processed: colorPicker("primary.700"),
  delivered: colorPicker("primary.700"),
  open: colorPicker("primary.700"),
  click: colorPicker("primary.700"),
  bounce: "#e74c3c",
  dropped: "#e74c3c",
  spammed: "#e74c3c",
  deffered: "#e74c3c",
};

function generateHotelInvoiceNumber(hotelCode = "DXB-RAD") {
  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, ""); // e.g., 20250506
  const randomSerial = Math.floor(1000 + Math.random() * 9000); // e.g., 4-digit serial
  return `${hotelCode}-${datePart}-${randomSerial}`;
}


const initialColDef = [
  {
    headerName: "Status",
    children: [
      // {
      //   headerName: "Overall Status",
      //   field: "Overall Status",
      //   enableRowGroup: true,
      // },
      {
        headerName: "ERP Match Status",
        field: "ERP Matching Status",
        enableRowGroup: true,
        formatType: "MATCH",
        ...handleCellRenderer(),
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["Match", "MismMISMATCHat"], // Predefined values for the selector
        },
        pivot: true,
        formatProps: {
          matchLogic: {
            matchColor: {
              Match: "green",
              MISMATCH: "red",
            },
            icon: {
              Match: <CheckCircleFilled />,
              MISMATCH: <CloseCircleFilled />,
            },
          },
          defaultText: "Not Available",
        },
      },
      {
        headerName: "Invoice Received Status",
        field: "Invoice Received Status",
        enableRowGroup: true,
        formatType: "MATCH",
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["Invoice Received", "Invoice Not Received"], // Predefined values for the selector
        },
        ...handleCellRenderer(),
        pivot: true,
        formatProps: {
          matchLogic: {
            matchColor: {
              "Invoice Received": "green",
              "Invoice Not Received": "red",
            },
            icon: {
              "Invoice Received": <CheckCircleFilled />,
              "Invoice Not Received": <CloseCircleFilled />,
            },
          },
          defaultText: "Not Available",
        },
      },
      {
        headerName: "Amount Matching Status",
        field: "Amount Matching Status",
        enableRowGroup: true,
        formatType: "MATCH",
        ...handleCellRenderer(),
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["Match", "MISMATCH"], // Predefined values for the selector
        },
        pivot: true,
        formatProps: {
          matchLogic: {
            matchColor: {
              Match: "green",
              MISMATCH: "red",
            },
            icon: {
              Match: <CheckCircleFilled />,
              MISMATCH: <CloseCircleFilled />,
            },
          },
          defaultText: "Not Available",
        },
      },
      {
        headerName: "Commission Status",
        field: "Commission Status",
        enableRowGroup: true,
        formatType: "MATCH",
        ...handleCellRenderer(),
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["Commission Received", "Commission Not Received"], // Predefined values for the selector
        },
        pivot: true,
        formatProps: {
          matchLogic: {
            matchColor: {
              "Commission Received": "green",
              "Commission Not Received": "red",
            },
            icon: {
              "Commission Received": <CheckCircleFilled />,
              "Commission Not Received": <CloseCircleFilled />,
            },
          },
          defaultText: "Not Available",
        },
      },
    ],
  },

  {
    headerName: "Booking Details",
    children: [
      {
        headerName: "SUPPLIER NAME",
        field: "SUPPLIER NAME",
        filter: "agTextColumnFilter",
        formatType: "ENTITY",
        columnGroupShow: ["open", "close"],
        ...handleCellRenderer(),
      },
      {
        headerName: "SUPPLIER CONFIRMATION NO.",
        field: "SUPPLIER CONFIRMATION NO.",
        filter: "agTextColumnFilter",
        ...handleCellRenderer(),
        columnGroupShow: ["open", "close"],
      },
      {
        headerName: "SUPPLIER REFERENCE NO.",
        field: "SUPPLIER REFERENCE NO.",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
      {
        headerName: "TBOH CONFIRMATION NO.",
        field: "TBOH CONFIRMATION NO.",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },

      {
        headerName: "HOTEL Code",
        field: "HOTEL Code",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
      {
        headerName: "Giata code",
        field: "Giata code",
        filter: "agNumberColumnFilter",
        enableValue: true,
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
      {
        headerName: "TBOM Reference Number",
        field: "TBOM Reference Number",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
      {
        headerName: "Type of Supplier",
        field: "Type of Supplier",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
      {
        headerName: "CHECK-IN DATE",
        field: "CHECK-IN DATE",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
      {
        headerName: "CHECK-OUT DATE",
        field: "CHECK-OUT DATE",
        filter: "agTextColumnFilter",
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },
      {
        headerName: "Month",
        field: "Monthj",
        filter: "agTextColumnFilter",
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },
      {
        headerName: "Guest Name",
        field: "Guest Name",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        ...handleCellRenderer(),
      },
    ],
  },

  {
    headerName: "Hotel Details",
    children: [
      {
        headerName: "Hotel Name",
        field: "Hotel Name",
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
        formatType: "ENTITY",
        ...handleCellRenderer(),
      },
      {
        headerName: "HCN",
        field: "HCN",
        enableRowGroup: true,
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
      },
      {
        headerName: "Mail Id",
        field: "Mail Id",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
      },

      {
        headerName: "Hotel City",
        field: "Hotel City",
        filter: "agDateColumnFilter",
        columnGroupShow: "open",
      },
      {
        headerName: "Country",
        field: "Country",
        filter: "agDateColumnFilter",
        columnGroupShow: "open",
      },

      {
        headerName: "Supplier registered Place",
        field: "Supplier registered Place",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
      },
    ],
  },

  {
    headerName: "Fare Details",
    children: [
      {
        headerName: "Net Supplier price USD",
        field: "Net Supplier price USD",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        formatType: "AMOUNT_USD",
        ...handleCellRenderer(),
      },
      {
        headerName: "Amt in AED",
        field: "Amt in AED",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "VAT Amt AED",
        field: "VAT Amt AED",
        enableRowGroup: true,
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "VAT Amt INR",
        field: "VAT Amt INR",
        enableRowGroup: true,
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
        formatType: "AMOUNT",
        ...handleCellRenderer(),
      },
    ],
  },

  {
    headerName: "Payment Details",
    children: [
      {
        headerName: "Trans ID",
        field: "Trans ID",
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
      },
      {
        headerName: "Net Settled",
        field: "Net Settled",
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "Difference amount",
        field: "Difference amount",
        enableRowGroup: true,
        filter: "agTextColumnFilter",
        columnGroupShow: ["open", "close"],
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
    ],
  },

  {
    headerName: "Comission Details",
    children: [
      {
        headerName: "Amt in AED",
        field: "Amt in AED",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "Amt in INR",
        field: "Amt in INR",
        filter: "agTextColumnFilter",
        columnGroupShow: "open",
        formatType: "AMOUNT",
        ...handleCellRenderer(),
      },
    ],
  },
  {
    headerName: "Invoice Details",
    children: [
      {
        headerName: "Hotel Name",
        field: "Hotel Name",
        formatType: "ENTITY",
        ...handleCellRenderer(),
        columnGroupShow: ["open", "close"],
      },
      {
        headerName: "Hotel Invoice Number",
        field: "Hotel Invoice Number",
        enableRowGroup: true,
        ...handleCellRenderer(),
        cellRenderer: (params: any) => {
          return params.data?.["Invoice Received Status"] === "Invoice Received"
            ? generateHotelInvoiceNumber()
            : "";
        },
        columnGroupShow: ["open", "close"],
      },
      {
        headerName: "VAT ID",
        field: "VAT ID",
        enableRowGroup: true,
        ...handleCellRenderer(),
        cellRenderer: (params: any) => {
          return params.data?.["Invoice Received Status"] === "Invoice Received"
            ? generateUAEVATID()
            : "";
        },
        columnGroupShow: ["open", "close"],
      },
      {
        headerName: "Hotel Address",
        field: "Hotel Address",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },
      {
        headerName: "HCN",
        field: "HCN",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },
      {
        headerName: "Guest Name",
        field: "Guest Name",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },
      {
        headerName: "Check In date",
        field: "Check In date",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },
      {
        headerName: "Check Out",
        field: "Check Out",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
      },

      {
        headerName: "VAT Amount",
        field: "VAT Amount",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "Municipal Tax",
        field: "Municipal Tax",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "Taxable Amount",
        field: "Taxable Amount",
        enableRowGroup: true,
        ...handleCellRenderer(),
        columnGroupShow: "open",
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
      },
      {
        headerName: "Invoice Amount",
        field: "Invoice Amount",
        enableRowGroup: true,
        columnGroupShow: "open",
        formatType: "AMOUNT_AED",
        ...handleCellRenderer(),
        ...handleCellRenderer(),
      },
    ],
  },
];

const EVENT_ICON_MAP = {
  processed: (color: any) => (
    <CheckOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
  delivered: (color: any) => (
    <DoneAllOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
  open: (color: any) => (
    <DraftsOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
  click: (color: any) => (
    <AdsClickOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
  acknowledge: (color: any) => (
    <AssignmentTurnedInIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
        marginRight: 28,
      }}
    />
  ),

  UNSUBSCRIBE: (color: any) => (
    <UnsubscribeOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
  SPAMMED: (color: any) => (
    <PrivacyTipOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
  bounce: (color: any) => (
    <CancelOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),

  dropped: (color: any) => (
    <MarkunreadMailboxOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),

  DEFFERED: (color: any) => (
    <ScheduleSendOutlinedIcon
      style={{
        fontSize: 18,
        color: color,
        cursor: "pointer",
      }}
    />
  ),
};

function formatDate(timestamp: any) {
  // Convert Unix timestamp to milliseconds
  const date = new Date(timestamp * 1000);

  // Use Moment.js to format the date
  const formattedDate = moment(date).format("DD-MM-YYYY, HH:MM A");

  return formattedDate;
}

export default function FlightRecon(props: any) {
  const [pdfViewerData, setPdfViewerData] = useState("");
  const [_, setCurrentPDFData] = useRecoilState(currentPDFData);
  const openSidebarRenderer = (params: any) => {
    setCurrentPDFData(params?.data);
    params.api.openToolPanel("pdfRenderer", { paramData: params });
  };

  const handleRenderEmailEvents = (eventData: any) => {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        {Object.entries(EVENT_ICON_MAP).map((emailEvent: any) => {
          return (
            <div
              style={{
                marginRight: 6,
                display: "flex",
                height: "100%",
                alignItems: "center",
              }}
            >
              <Tooltip
                placement="bottom"
                title={
                  <div>
                    <Typography
                      style={{ color: "white", marginBottom: 12 }}
                      variant="xs"
                    >
                      {emailEvent[0]}
                    </Typography>
                    {(eventData?.[emailEvent[0]] || []).map((reader: any) => {
                      return (
                        <Tag key={reader.email} style={{ fontSize: 10 }}>
                          {reader.email} at {formatDate(reader.timestamp)}
                        </Tag>
                      );
                    })}
                  </div>
                }
              >
                {emailEvent[1](
                  eventData?.[emailEvent[0]]
                    ? colorOfEmailEvent[emailEvent[0]]
                    : "rgb(192 194 197)"
                )}
              </Tooltip>
            </div>
          );
        })}
      </div>
    );
  };
  const invoiceLinkMap: any = {
    "Radisson Blu Hotel & ResortAbu Dhabi Corniche":
      "https://files.finkraft.ai/4f17786e73cb83165f09dd932dc2a50a",
    "Golden Tulip Deira Hotel":
      "https://files.finkraft.ai/14d3f78b8834369243fbe09f852c530a",
    "Jannah Hotel Apartments & Villas":
      "https://files.finkraft.ai/dda19c95b82e401c13838aa32394f9ff",
    Hilton: "https://files.finkraft.ai/a47cf2dc9c8e905cfa9f907ce8f3ccfe",
  };

  // Column definitions for Divide PNR Data tab
  const dividePNRColumnDefs = [
    {
      headerName: "Booking Date",
      field: "BookingDate",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Parent PNR",
      field: "ParentPNR",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Divided Date",
      field: "DividedDate",
      formatType: "DATE",
      filter: "agDateColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Child PNR",
      field: "ChildPNR",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Payment Amount",
      field: "PaymentAmount",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Currency Code",
      field: "CurrencyCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Payment Method Code",
      field: "PaymentMethodCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Source Organization Code",
      field: "SourceOrganizationCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Booking Promo Code",
      field: "BookingPromoCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Source Agent Code",
      field: "SourceAgentCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
  ];

  // Column definitions for SAP_Format_KGColumnDefs tab
  const SAP_Format_KGColumnDefs = [
    {
      headerName: "Document Date",
      field: "Document Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Posting Date",
      field: "Posting Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference",
      field: "Reference",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Header Text",
      field: "Header Text",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "GL account - Vendor",
      field: "GL account - Vendor",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Amount-HEADER",
      field: "Amount-HEADER",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Currency",
      field: "-",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Tax Code-31 Head",
      field: "Tax Code-31 Head",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Business Place",
      field: "Business Place",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Section Code",
      field: "Section Code",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Pay terms",
      field: "Pay terms",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Assignment",
      field: "Assignment",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Text",
      field: "Text",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference Key 1",
      field: "Reference Key 1",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference Key 2",
      field: "Reference Key 2",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference Key 3",
      field: "Reference Key 3",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Partner Bank Type",
      field: "Partner Bank Type",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-GL account",
      field: "Item-GL account",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Amount",
      field: "Item-Amount",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Tax Code-Item",
      field: "Tax Code-Item",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Business Place",
      field: "Item-Business Place",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Cost Center",
      field: "Item-Cost Center",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item- profit cen",
      field: "Item- profit cen",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Order",
      field: "Order",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Assignment",
      field: "Item-Assignment",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Text",
      field: "Item-Text",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-REF1",
      field: "Item-REF1",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-REF2",
      field: "Item-REF2",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-REF3",
      field: "Item-REF3",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-vbund_g",
      field: "Item-vbund_g",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item Pay terms",
      field: "Item Pay terms",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "WBS ELEMENT",
      field: "WBS ELEMENT",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
  ];

  // Column definitions for Base Booking 4 tab
  const SAP_Format_KRColumnDefs = [
    {
      headerName: "Document Date",
      field: "Document Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Posting Date",
      field: "Posting Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference",
      field: "Reference",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Header Text",
      field: "Header Text",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "GL account - Vendor",
      field: "GL account - Vendor",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Amount-HEADER",
      field: "Amount-HEADER",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Currency",
      field: "-",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Tax Code-31 Head",
      field: "Tax Code-31 Head",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Business Place",
      field: "Business Place",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Section Code",
      field: "Section Code",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Pay terms",
      field: "Pay terms",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Assignment",
      field: "Assignment",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Text",
      field: "Text",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference Key 1",
      field: "Reference Key 1",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference Key 2",
      field: "Reference Key 2",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Reference Key 3",
      field: "Reference Key 3",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Partner Bank Type",
      field: "Partner Bank Type",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-GL account",
      field: "Item-GL account",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Amount",
      field: "Item-Amount",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Tax Code-Item",
      field: "Tax Code-Item",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Business Place",
      field: "Item-Business Place",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Cost Center",
      field: "Item-Cost Center",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item- profit cen",
      field: "Item- profit cen",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Order",
      field: "Order",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Assignment",
      field: "Item-Assignment",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-Text",
      field: "Item-Text",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-REF1",
      field: "Item-REF1",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-REF2",
      field: "Item-REF2",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-REF3",
      field: "Item-REF3",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item-vbund_g",
      field: "Item-vbund_g",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Item Pay terms",
      field: "Item Pay terms",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "WBS ELEMENT",
      field: "WBS ELEMENT",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
  ];

  // Get tab-specific columnDefs and rowData (only for Divide PNR Data, Base Booking 3, Base Booking 4)
  // Transaction Data uses default columnDefs and JsonData from demo.json
  const { columnDefs: tabColumnDefs, rowData: tabRowData } = useMemo(() => {
    if (!props.activeTab) {
      return { columnDefs: null, rowData: null };
    }
    switch (props.activeTab) {
      case "Divide PNR Data":
        return {
          columnDefs: dividePNRColumnDefs,
          rowData: dividePNRData,
        };
      case "SAP_Format_KG-Invoice":
        return {
          columnDefs: SAP_Format_KGColumnDefs,
          rowData: baseBooking3Data,
        };
      case "SAP_Format_KR-Refund":
        return {
          columnDefs: SAP_Format_KRColumnDefs,
          rowData: baseBooking4Data,
        };
      default:
        // Transaction Data and other tabs use default columnDefs
        return { columnDefs: null, rowData: null };
    }
  }, [props.activeTab]);

  // Column definitions for Transaction Data tab
  const defaultColumnDefs = [
    {
      headerName: "Transaction Date",
      field: "Transaction Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Name",
      field: "Name",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Payment Date",
      field: "PaymentDtm",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
          ...handleCellRenderer(),
    },
    {
      headerName: "Payment Method Code",
      field: "PaymentMethodCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Payment Amount",
      field: "PaymentAmount",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Payment Number",
      field: "PaymentNumber",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Booking Date",
      field: "BookingDate",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
          ...handleCellRenderer(),
    },
    {
      headerName: "Record Locator",
      field: "RecordLocator",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Source Organization Code",
      field: "SourceOrganizationCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Booking Promo Code",
      field: "BookingPromoCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Received By",
      field: "ReceivedBy",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Source Agent Code",
      field: "SourceAgentCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "International",
      field: "International",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Currency Code",
      field: "CurrencyCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Pax Count",
      field: "PaxCount",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Name1",
      field: "Name1",
      filter: "agTextColumnFilter",
      formatType: "PERSON",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Email Address",
      field: "EmailAddress",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Home Phone",
      field: "HomePhone",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "GDS Record Code",
      field: "GDS_recordcode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "GDS Record Locator",
      field: "GDS_recordlocator",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "GDS Booking System Code",
      field: "GDS_BookingSystemCode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Payment Status",
      field: "PaymentStatus",
          filter: "agSetColumnFilter",
          filterParams: {
        values: ["Approved", "Pending", "Failed"],
      },
      enableRowGroup: true,
          ...handleCellRenderer(),
    },
    {
      headerName: "Total",
      field: "Total",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Base Fare",
      field: "BaseFare",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "First Leg",
      field: "First Leg",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "First Leg Flight No",
      field: "First Leg Flight No",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
          ...handleCellRenderer(),
    },
    {
      headerName: "First Leg Dep Date",
      field: "First Leg Dep Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Second Leg",
      field: "Second Leg",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
          ...handleCellRenderer(),
    },
    {
      headerName: "Second Leg Flight No",
      field: "Second Leg Flight No",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Second Leg Dep Date",
      field: "Second Leg Dep Date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Third Leg",
      field: "Third Leg",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
          ...handleCellRenderer(),
    },
    {
      headerName: "Third Leg Flight No",
      field: "Third Leg Flight No",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Third Leg Dep Date",
      field: "Third Leg Dep Date",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Fourth Leg",
      field: "Fourth Leg",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Fourth Leg Flight No",
      field: "Fourth Leg Flight No",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Fourth Leg Dep Date",
      field: "Fourth Leg Dep Date",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Fifth Leg",
      field: "Fifth Leg",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Fifth Leg Flight No",
      field: "Fifth Leg Flight No",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Fifth Leg Dep Date",
      field: "Fifth Leg Dep Date",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Sixth Leg",
      field: "Sixth Leg",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Sixth Leg Flight No",
      field: "Sixth Leg Flight No",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Sixth Leg Dep Date",
      field: "Sixth Leg Dep Date",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "Upload Date",
      field: "upload date",
      filter: "agDateColumnFilter",
      formatType: "DATE",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    {
      headerName: "PROT",
      field: "PROT",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
    // Add all the fee/tax fields as number columns
    {
      headerName: "ABH",
      field: "ABH",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "AE",
      field: "AE",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "ASF",
      field: "ASF",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "IGT",
      field: "IGT",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "RAF",
      field: "RAF",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "RCF",
      field: "RCF",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "TF",
      field: "TF",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "UDF",
      field: "UDF",
      filter: "agNumberColumnFilter",
      enableRowGroup: true,
      formatType: "AMOUNT",
      ...handleCellRenderer(),
    },
    {
      headerName: "Connection Adjustment Amount",
      field: "ConnectionAdjustmentAmount",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      ...handleCellRenderer(),
    },
  ];
  // Use tab-specific data for Divide PNR Data, Base Booking 3, Base Booking 4
  // Use default columnDefs and JsonData for Transaction Data tab
  const finalColumnDefs = tabColumnDefs || defaultColumnDefs;
  const finalRowData = tabRowData || JsonData;

  return (
    <div className="FlightRecon" style={{ height: "100%" }}>
      <AgTableClient
        /* @ts-ignore */
        columnDefs={finalColumnDefs}
        rowData={finalRowData}
        groupPanel
        enablePdfViewer={!tabColumnDefs}
        pdfDataKey="Invoice_Link"
      />
    </div>
  );
}
